<template>
  <div>
    <search-sale-monthly-performance-by-month-list @submit="submitSearch" />

    <div class="simiot-table-operation-buttons">
      <a-space>
        <a-button type="primary" @click="exportData">导出</a-button>
        <a-button v-if="canRecalculate" @click="recalculate">重新计算</a-button>
      </a-space>
    </div>

    <a-table
      :scroll="{ x: 'max-content' }"
      size="middle"
      :columns="columns"
      :data-source="data"
      :loading="loading"
      :pagination="false"
      :row-class-name="rowClassName"
      @change="sortChange"
      row-key="id"
    >
      <div slot="sale_name" slot-scope="sale_name, record">
        <router-link :to="{ name: 'sale_monthly_performance_info', params: { id: record.id }}">
          {{ record.sale_name }}
        </router-link>
      </div>

      <div slot="action" slot-scope="text, record">
        <a v-if="canRecalculate" @click="recalculateSale(record)">重新计算</a>
      </div>
    </a-table>

    <pagination
      v-show="pagination.total_count > 0"
      :total-count="pagination.total_count"
      :page.sync="query.page"
      :per-page.sync="query.per_page"
      @change="fetchData"
    />
  </div>
</template>

<script>
import Pagination from '@/components/Pagination'
import { findSaleMonthlyPerformancesByMonth, exportSaleMonthlyPerformancesByMonth, recalculateSaleMonthlyPerformance } from '@/api/sale_monthly_performance'
import { hasPermission } from '@/utils/permission'
import SearchSaleMonthlyPerformanceByMonthList from '@/views/sale_monthly_performances/admin/ByMonthSearch'
import { formatFloat } from '@/utils/filter'
import { exportExcel } from '@/api/excel'
import { canViewSaleCostProfit, cardFloorDiff, floorStatColumns, hasNegativeFloorDiff, monthlyFloorPairs, packageFloorDiff, salePerformanceRecalculateContent } from '@/utils/sale_monthly_performance'

export default {
  name: 'SaleMonthlyPerformanceByMonthList',
  components: {
    SearchSaleMonthlyPerformanceByMonthList,
    Pagination
  },
  data() {
    return {
      data: [],
      loading: true,
      pagination: {
        total_count: 0
      },
      query: {},
      sort: {
        sort_field: '',
        sort_order: ''
      }
    }
  },
  computed: {
    canViewCostProfit() {
      return canViewSaleCostProfit(this.$store.getters.userRole)
    },
    canRecalculate() {
      return hasPermission('sale_monthly_performances/recalculate')
    },
    columns() {
      const cols = [
        {
          title: '销售',
          dataIndex: 'sale_name',
          width: 150,
          scopedSlots: { customRender: 'sale_name' }
        }
      ]
      if (this.canRecalculate) {
        cols.push({
          title: '操作',
          width: 100,
          scopedSlots: { customRender: 'action' }
        })
      }
      cols.push(
        {
          title: '销售业绩总额(元)',
          customRender: formatFloat,
          sorter: true,
          sortOrder: this.sort.sort_field === 'total_amount' ? this.sort.sort_order : false,
          dataIndex: 'total_amount'
        }
      )
      if (this.canViewCostProfit) {
        cols.push(
          {
            title: '成本核算(元)',
            customRender: formatFloat,
            sorter: true,
            sortOrder: this.sort.sort_field === 'additional_cost' ? this.sort.sort_order : false,
            dataIndex: 'additional_cost'
          },
          {
            title: '毛利核算(元)',
            customRender: formatFloat,
            sorter: true,
            sortOrder: this.sort.sort_field === 'profit' ? this.sort.sort_order : false,
            dataIndex: 'profit'
          },
          {
            title: '新增毛利(元)',
            customRender: formatFloat,
            sorter: true,
            sortOrder: this.sort.sort_field === 'additional_profit' ? this.sort.sort_order : false,
            dataIndex: 'additional_profit'
          },
          {
            title: '存量毛利(元)',
            customRender: formatFloat,
            sorter: true,
            sortOrder: this.sort.sort_field === 'stored_profit' ? this.sort.sort_order : false,
            dataIndex: 'stored_profit'
          }
        )
      }
      cols.push(
        {
          title: '新增业绩-卡板(元)',
          customRender: formatFloat,
          sorter: true,
          sortOrder: this.sort.sort_field === 'additional_card_amount' ? this.sort.sort_order : false,
          dataIndex: 'additional_card_amount'
        },
        {
          title: '卡板底价(元)',
          customRender: formatFloat,
          sorter: true,
          sortOrder: this.sort.sort_field === 'card_floor_amount' ? this.sort.sort_order : false,
          dataIndex: 'card_floor_amount'
        },
        {
          title: '卡板底价差(元)',
          customRender: (text, record) => formatFloat(cardFloorDiff(record.additional_card_amount, record.card_floor_amount)),
          sorter: true,
          sortOrder: this.sort.sort_field === 'card_floor_diff' ? this.sort.sort_order : false,
          dataIndex: 'card_floor_diff'
        },
        {
          title: '新增业绩-套餐(元)',
          customRender: formatFloat,
          sorter: true,
          sortOrder: this.sort.sort_field === 'additional_product_amount' ? this.sort.sort_order : false,
          dataIndex: 'additional_product_amount'
        },
        {
          title: '套餐底价(元)',
          customRender: formatFloat,
          sorter: true,
          sortOrder: this.sort.sort_field === 'floor_amount' ? this.sort.sort_order : false,
          dataIndex: 'floor_amount'
        },
        {
          title: '套餐底价差(元)',
          customRender: (text, record) => formatFloat(packageFloorDiff(record.additional_product_amount, record.floor_amount)),
          sorter: true,
          sortOrder: this.sort.sort_field === 'sale_profit' ? this.sort.sort_order : false,
          dataIndex: 'sale_profit'
        },
        {
          title: '新增业绩-eSIM(元)',
          customRender: formatFloat,
          sorter: true,
          sortOrder: this.sort.sort_field === 'additional_esim_amount' ? this.sort.sort_order : false,
          dataIndex: 'additional_esim_amount'
        },
        ...floorStatColumns(formatFloat, this.sort, 'esim_floor_amount', 'esim_floor_diff', 'eSIM底价(元)', 'eSIM底价差(元)', 'additional_esim_amount'),
        {
          title: '新增业绩-Profile(元)',
          customRender: formatFloat,
          sorter: true,
          sortOrder: this.sort.sort_field === 'additional_profile_amount' ? this.sort.sort_order : false,
          dataIndex: 'additional_profile_amount'
        },
        ...floorStatColumns(formatFloat, this.sort, 'profile_floor_amount', 'profile_floor_diff', 'Profile底价(元)', 'Profile底价差(元)', 'additional_profile_amount'),
        {
          title: '新增业绩-模组(元)',
          customRender: formatFloat,
          sorter: true,
          sortOrder: this.sort.sort_field === 'additional_module_amount' ? this.sort.sort_order : false,
          dataIndex: 'additional_module_amount'
        },
        ...floorStatColumns(formatFloat, this.sort, 'module_floor_amount', 'module_floor_diff', '模组底价(元)', '模组底价差(元)', 'additional_module_amount'),
        {
          title: '新增业绩-CPE(元)',
          customRender: formatFloat,
          sorter: true,
          sortOrder: this.sort.sort_field === 'additional_cpe_amount' ? this.sort.sort_order : false,
          dataIndex: 'additional_cpe_amount'
        },
        ...floorStatColumns(formatFloat, this.sort, 'cpe_floor_amount', 'cpe_floor_diff', 'CPE底价(元)', 'CPE底价差(元)', 'additional_cpe_amount'),
        {
          title: '快递费(元)',
          customRender: formatFloat,
          sorter: true,
          sortOrder: this.sort.sort_field === 'shipping_fee' ? this.sort.sort_order : false,
          dataIndex: 'shipping_fee'
        },
        {
          title: '存量业绩(元)',
          customRender: formatFloat,
          sorter: true,
          sortOrder: this.sort.sort_field === 'stored_amount' ? this.sort.sort_order : false,
          dataIndex: 'stored_amount'
        },
        {
          title: '新增卡量',
          sorter: true,
          sortOrder: this.sort.sort_field === 'new_cards_count' ? this.sort.sort_order : false,
          dataIndex: 'new_cards_count'
        },
        {
          title: '新增eSIM数量',
          sorter: true,
          sortOrder: this.sort.sort_field === 'new_esims_count' ? this.sort.sort_order : false,
          dataIndex: 'new_esims_count'
        },
        {
          title: '新增Profile数量',
          sorter: true,
          sortOrder: this.sort.sort_field === 'new_profiles_count' ? this.sort.sort_order : false,
          dataIndex: 'new_profiles_count'
        },
        {
          title: '新增模组数量',
          sorter: true,
          sortOrder: this.sort.sort_field === 'new_modules_count' ? this.sort.sort_order : false,
          dataIndex: 'new_modules_count'
        },
        {
          title: '新增CPE数量',
          sorter: true,
          sortOrder: this.sort.sort_field === 'new_cpes_count' ? this.sort.sort_order : false,
          dataIndex: 'new_cpes_count'
        },
        {
          title: '新增客户数',
          sorter: true,
          sortOrder: this.sort.sort_field === 'new_agents_count' ? this.sort.sort_order : false,
          dataIndex: 'new_agents_count'
        },
        {
          title: '客户总数',
          sorter: true,
          sortOrder: this.sort.sort_field === 'total_agents_count' ? this.sort.sort_order : false,
          dataIndex: 'total_agents_count'
        }
      )
      return cols
    }
  },
  created() {
    this.fetchData()
    this.setBreadcrumb()
  },
  methods: {
    setBreadcrumb() {
      this.$store.dispatch('SetDynamicBreadCrumbsPlaceholder', { ':month': this.$route.query.month })
    },

    submitSearch(search) {
      // 重置排序
      this.sort = this.$options.data().sort
      // 赋值搜索条件，保留 per_page
      this.query = Object.assign({},
        this.$options.data().query,
        { per_page: this.query.per_page },
        search)
      this.fetchData()
    },

    sortChange(pagination, filters, sorter) {
      this.sort.sort_field = sorter.field
      this.sort.sort_order = sorter.order
      // 跳转第一页
      this.query.page = 1
      this.fetchData()
    },

    fetchData() {
      this.loading = true
      findSaleMonthlyPerformancesByMonth(Object.assign({ month: this.$route.query.month },
        this.query, this.sort)).then((res) => {
        if (res.code === 0) {
          this.data = res.data
          this.pagination = res.pagination
        }
        this.loading = false
      })
    },

    rowClassName(record) {
      if (hasNegativeFloorDiff(record, monthlyFloorPairs)) {
        return 'sale-profit-negative-row'
      }
      if (this.canViewCostProfit && (
        (record.profit != null && Number(record.profit) < 0) ||
        (record.additional_profit != null && Number(record.additional_profit) < 0) ||
        (record.stored_profit != null && Number(record.stored_profit) < 0)
      )) {
        return 'sale-profit-negative-row'
      }
      return ''
    },

    exportData() {
      exportSaleMonthlyPerformancesByMonth(Object.assign({ month: this.$route.query.month }, this.query)).then((res) => {
        if (res.code === 0 && res.data.is_direct_download) {
          exportExcel(res.data.id, res.data.file_name)
        }
      })
    },

    recalculate() {
      const month = this.$route.query.month
      this.$confirm({
        title: `重新计算 ${month} 的销售业绩？`,
        content: salePerformanceRecalculateContent(),
        onOk: () => recalculateSaleMonthlyPerformance({ month })
      })
    },

    recalculateSale(record) {
      this.$confirm({
        title: `重新计算 ${record.sale_name} 本月业绩？`,
        content: salePerformanceRecalculateContent({ useLatestFloorPrice: true }),
        onOk: () => recalculateSaleMonthlyPerformance({ sale_monthly_performance_id: record.id })
      })
    }
  }
}
</script>

<style>
.sale-profit-negative-row td {
  color: #f5222d !important;
}
</style>

